/// <reference types="vite/client" />

// The only code that calls `fetch`: every generated hook routes through `arbBotFetcher`
// (orval.config.ts `override.mutator`). It resolves the base URL, sends the stored key, unwraps
// JSON, throws a typed error on any non-2xx, and parses every success body with its operation's
// generated zod schema, so a backend that drifts from the contract fails here, loudly, instead of
// rendering `undefined`. [[spec-in-repo-react-frontend-for-arb-bot-with-an-openapi-and-orval-contract]] R4

import { z } from "zod";

import { currentKey, signOut, touchSession } from "@/lib/auth/session";

import { responseSchemaFor } from "./contract";

export const SECRET_KEY_HEADER = "x-secret-key";

export class ApiError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(status: number, statusText: string, body: unknown) {
        super(`The bot answered ${status} ${statusText}`.trim());
        this.name = "ApiError";
        this.status = status;
        this.body = body;
    }
}

export class ContractViolationError extends Error {
    readonly operation: string;
    readonly issues: z.core.$ZodIssue[];

    constructor(operation: string, issues: z.core.$ZodIssue[]) {
        super(`${operation} answered outside the API contract: ${z.prettifyError(new z.ZodError(issues))}`);
        this.name = "ContractViolationError";
        this.operation = operation;
        this.issues = issues;
    }
}

/** Generated URLs are absolute API paths; `VITE_API_BASE_URL` points them at a split dev origin. */
export function resolveApiUrl(path: string): string {
    const base = import.meta.env.VITE_API_BASE_URL ?? "";
    return new URL(`${base}${path}`, window.location.origin).toString();
}

export const arbBotFetcher = async <T>(url: string, options: RequestInit): Promise<T> => {
    const method = (options.method ?? "GET").toUpperCase();
    const headers = new Headers(options.headers);
    headers.set("Accept", "application/json");
    // A caller-supplied key is a login attempt, which must neither use nor end the stored session.
    const usesStoredSession = !headers.has(SECRET_KEY_HEADER);
    const storedKey = usesStoredSession ? currentKey() : null;
    if (storedKey !== null) headers.set(SECRET_KEY_HEADER, storedKey);

    const response = await fetch(resolveApiUrl(url), { ...options, headers });
    const body = await readBody(response);

    if (!response.ok) {
        if (response.status === 401 && storedKey !== null) signOut("rejected");
        throw new ApiError(response.status, response.statusText, body);
    }
    if (storedKey !== null) touchSession();

    const path = new URL(url, "http://contract").pathname;
    const schema = responseSchemaFor(method, path);
    const operation = `${method} ${path}`;
    if (schema === undefined) throw new ContractViolationError(operation, [unknownOperation(operation)]);
    const parsed = schema.safeParse(body);
    if (!parsed.success) throw new ContractViolationError(operation, parsed.error.issues);
    return parsed.data as T;
};

async function readBody(response: Response): Promise<unknown> {
    if (response.status === 204) return null;
    const text = await response.text();
    if (text.length === 0) return null;
    const isJson = (response.headers.get("content-type") ?? "").includes("json");
    if (!isJson) return text;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

function unknownOperation(operation: string): z.core.$ZodIssue {
    return { code: "custom", path: [], message: `${operation} is not in the API contract`, input: undefined };
}
